import { writable } from 'svelte/store';

/**
 * Creates a selection store over a `Set` of item keys.
 *
 * Selection is tracked by key rather than by object reference because the
 * `Table` component hands cell components copies of each row.
 *
 * @param {(item: Object) => string} [getKey] Extracts an item's key. Defaults to `rulename`.
 * @returns {{
 *   subscribe: import('svelte/store').Readable<Set<string>>['subscribe'],
 *   toggle: (item: Object) => void,
 *   selectAll: (items: Object[]) => void,
 *   clear: () => void,
 *   prune: (items: Object[]) => void
 * }}
 *
 * @example
 * const selection = createSelection();
 * selection.toggle(image);
 * $selection.has(image.rulename); // true
 */
export const createSelection = (getKey = (item) => item.rulename) => {
  const { subscribe, set, update } = writable(new Set());

  return {
    subscribe,

    /** Adds the item's key when absent, removes it when present. */
    toggle: (item) =>
      update((current) => {
        const key = getKey(item);
        const next = new Set(current);

        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }

        return next;
      }),

    /** Replaces the selection with exactly the given items. */
    selectAll: (items) => set(new Set(items.map(getKey))),

    /** Empties the selection. */
    clear: () => set(new Set()),

    /** Drops any selected key that is not present in `items`. */
    prune: (items) => {
      const visible = new Set(items.map(getKey));

      update((current) => new Set([...current].filter((key) => visible.has(key))));
    }
  };
};

/**
 * Creates a selection store over a `Map` of key → projection.
 *
 * Unlike `createSelection`, this retains a projection of each selected item, so
 * the selection can be acted on without the source array still holding it. That
 * is what lets a selection survive pagination: `createSelection` stores keys only
 * and needs `selectedItems` to resolve them against the loaded page, so anything
 * off-page resolves to nothing.
 *
 * There is deliberately no `prune`. Pruning exists to scope a selection to what
 * is loaded, which is the behaviour this store is here to avoid.
 *
 * `project` must be idempotent: callers re-select already-projected items (e.g.
 * retaining the failures from a bulk run), so projecting a projection has to be
 * a no-op. Picking a fixed set of fields satisfies this.
 *
 * @param {(item: Object) => string} getKey Extracts an item's key.
 * @param {(item: Object) => Object} [project] Reduces an item to what must be retained.
 * @returns {{
 *   subscribe: import('svelte/store').Readable<Map<string, Object>>['subscribe'],
 *   toggle: (item: Object) => void,
 *   add: (items: Object[]) => void,
 *   selectAll: (items: Object[]) => void,
 *   clear: () => void
 * }}
 *
 * @example
 * const selection = createItemSelection(
 *   (user) => user.email,
 *   ({ email, removed }) => ({ email, removed })
 * );
 *
 * selection.toggle(user);
 * $selection.has(user.email); // true — Map.has, so templates read the same as createSelection
 * [...$selection.values()];   // the retained projections, whatever page they came from
 */
export const createItemSelection = (getKey, project = (item) => item) => {
  const { subscribe, set, update } = writable(new Map());

  return {
    subscribe,

    /** Adds the item's projection when absent, removes it when present. */
    toggle: (item) =>
      update((current) => {
        const key = getKey(item);
        const next = new Map(current);

        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, project(item));
        }

        return next;
      }),

    /**
     * Adds the given items, keeping everything already selected. Use this rather
     * than `selectAll` for a page-scoped select-all, which must not discard
     * selections made on other pages.
     */
    add: (items) =>
      update((current) => {
        const next = new Map(current);

        items.forEach((item) => next.set(getKey(item), project(item)));

        return next;
      }),

    /** Replaces the selection with exactly the given items. */
    selectAll: (items) => set(new Map(items.map((item) => [getKey(item), project(item)]))),

    /** Empties the selection. */
    clear: () => set(new Map())
  };
};

/**
 * Resolves selected keys back to their full source objects.
 *
 * Always call this against a page's unfiltered source array so delete payloads
 * never carry the `$sortOn` / `$expanded` fields `Table` adds to row copies.
 *
 * @param {Object[]} items The source objects.
 * @param {Set<string>} keys The currently selected keys.
 * @param {(item: Object) => string} [getKey] Extracts an item's key.
 * @returns {Object[]}
 */
export const selectedItems = (items, keys, getKey = (item) => item.rulename) =>
  items.filter((item) => keys.has(getKey(item)));
